import { Box, Heading, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'components/v4/Image';

// Whether an element's text is cut off, kept current as its width changes.
const useIsTruncated = (text) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const measure = useCallback(() => {
        const element = ref.current;
        setIsTruncated(
            !!element &&
                (element.scrollHeight - element.clientHeight > 1 ||
                    element.scrollWidth - element.clientWidth > 1),
        );
    }, []);

    useEffect(() => {
        measure();
        const element = ref.current;
        if (!element) return undefined;

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [text, measure]);

    return [ref, isTruncated];
};

/**
 * (3158) The body every service card shares, so the cards in a section are
 * compact and line up: logo and name on the first line, the status pill under
 * the name as on the old dashboard's tiles, then any detail rows in small
 * type. A long name truncates, with the full name in a tooltip.
 *
 * @param {object} props
 * @param {string} props.icon - logo src
 * @param {string} [props.iconClassName]
 * @param {string} props.name
 * @param {import('react').ReactNode} props.status - the status pill
 * @param {boolean} [props.isStatusInline] - the pill beside the name instead,
 *   as on an app auth
 * @param {import('react').ReactNode} [props.children] - detail rows
 */
const ServiceCardLayout = ({
    icon,
    iconClassName,
    name,
    status,
    isStatusInline = false,
    children,
}) => {
    const [nameRef, isNameTruncated] = useIsTruncated(name);

    return (
        <HStack align="flex-start" spacing={3} width="100%" minW={0}>
            <Image
                src={icon}
                className={iconClassName}
                boxSize="30px"
                objectFit="contain"
                flexShrink={0}
            />
            <VStack align="flex-start" spacing={1} flex="1" minW={0}>
                <HStack spacing={2} width="100%" minW={0} minH="30px">
                    <Tooltip
                        hasArrow
                        isDisabled={!isNameTruncated}
                        label={name}
                        placement="bottom-start">
                        <Heading
                            ref={nameRef}
                            as="h4"
                            fontSize="md"
                            noOfLines={1}
                            minW={0}>
                            {name}
                        </Heading>
                    </Tooltip>
                    {isStatusInline && status && (
                        <Box flexShrink={0}>{status}</Box>
                    )}
                </HStack>
                {!isStatusInline && status && <Box maxW="100%">{status}</Box>}
                {children}
            </VStack>
        </HStack>
    );
};

/** (3158) A label and value on one line under the card's name. */
export const ServiceCardDetail = ({ label, children }) => (
    <HStack spacing={1} width="100%" minW={0} fontSize="sm">
        <Text as="span" fontWeight="medium" flexShrink={0} marginBottom={0}>
            {label}
        </Text>
        <Text
            as="span"
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            marginBottom={0}>
            {children}
        </Text>
    </HStack>
);

export default ServiceCardLayout;
